import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Upload, X, ImageOff, ChevronLeft, Handshake, ExternalLink } from "lucide-react";

const MAX_BYTES = 4 * 1024 * 1024; // 4MB — server auto-optimises after upload

/**
 * Admin editor for the public /sponsor proposal page. Loads the current
 * document, lets the admin edit every text block and swap every photo,
 * then persists via PUT /sponsorship-proposal. The server auto-optimises
 * every uploaded image before storing so payload stays tiny.
 */
export default function AdminSponsorshipProposal() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sponsorship-proposal");
        setP(data);
      } catch (e) {
        toast.error("Could not load proposal");
      }
    })();
  }, []);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setTier = (i, k, v) => setP((prev) => {
    const tiers = [...(prev.tiers || [])];
    tiers[i] = { ...tiers[i], [k]: v };
    return { ...prev, tiers };
  });
  const addTier = () => setP((prev) => ({
    ...prev,
    tiers: [...(prev.tiers || []), { name: "", price: "", term: "", bullets: [], image_url: "" }],
  }));
  const removeTier = (i) => setP((prev) => ({
    ...prev,
    tiers: (prev.tiers || []).filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    setSaving(true);
    try {
      // Split multiline textareas back to arrays on save
      const payload = {
        ...p,
        why_bullets: (p.why_bullets || []).filter(Boolean),
        teams_image_urls: (p.teams_image_urls || []).filter(Boolean),
        tiers: (p.tiers || []).map((t) => ({ ...t, bullets: (t.bullets || []).filter(Boolean) })),
        team_members: (p.team_members || []).filter((m) => (m.name || m.role || m.image_url)),
        form_hero_image_urls: (p.form_hero_image_urls || []).filter(Boolean),
        form_extra_options: (p.form_extra_options || []).filter(Boolean),
      };
      const { data } = await api.put("/sponsorship-proposal", payload);
      setP(data);
      toast.success("Proposal saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!p) {
    return (
      <AppShell variant="admin">
        <div className="mx-auto max-w-4xl px-6 py-10 text-muted-foreground">Loading proposal…</div>
      </AppShell>
    );
  }

  return (
    <AppShell scope="admin">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-proposal-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link to="/a/sponsors" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:underline">
              <ChevronLeft className="h-3 w-3" /> Back to sponsor partners
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Handshake className="h-7 w-7 text-[hsl(var(--accent))]" /> Sponsorship proposal
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Edit the public <Link to="/sponsor" className="underline">/sponsor</Link> page shown when visitors tap "Become a sponsor" on the landing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-full">
              <a href="/sponsor" target="_blank" rel="noreferrer" data-testid="proposal-preview-btn">
                Preview <ExternalLink className="h-3.5 w-3.5 ml-1" />
              </a>
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90" data-testid="proposal-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save proposal"}
            </Button>
          </div>
        </div>

        {/* Cover */}
        <Section title="Cover">
          <Row label="Year" value={p.cover_year} onChange={(v) => set("cover_year", v)} />
          <Row label="Title" value={p.cover_title} onChange={(v) => set("cover_title", v)} />
          <RowMultiline label="Intro paragraph" value={p.cover_intro} onChange={(v) => set("cover_intro", v)} rows={4} />
          <PhotoField label="Cover photo" value={p.cover_image_url} onChange={(v) => set("cover_image_url", v)} />
        </Section>

        {/* Why sponsor */}
        <Section title="Why sponsor?">
          <RowMultiline label="Intro paragraph" value={p.why_intro} onChange={(v) => set("why_intro", v)} rows={5} />
          <RowMultiline label="Impact paragraph" value={p.why_impact_intro} onChange={(v) => set("why_impact_intro", v)} rows={2} />
          <BulletsField
            label="Impact bullets"
            values={p.why_bullets || []}
            onChange={(vals) => set("why_bullets", vals)}
            hint="One bullet per line. Indent sub-bullets with 2 spaces + •."
          />
          <Row label="Footnote" value={p.why_footnote} onChange={(v) => set("why_footnote", v)} />
          <PhotoField label="Why-sponsor photo" value={p.why_image_url} onChange={(v) => set("why_image_url", v)} />
        </Section>

        {/* History */}
        <Section title="History">
          <RowMultiline label="History text" value={p.history_text} onChange={(v) => set("history_text", v)} rows={8} />
        </Section>

        {/* Teams */}
        <Section title="Our teams">
          <RowMultiline label="Teams intro" value={p.teams_intro} onChange={(v) => set("teams_intro", v)} rows={4} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <PhotoTile
                key={`team-photo-${i}`}
                value={(p.teams_image_urls || [])[i] || ""}
                onChange={(v) => {
                  const arr = [...(p.teams_image_urls || ["", "", "", ""])];
                  while (arr.length < 4) arr.push("");
                  arr[i] = v;
                  set("teams_image_urls", arr);
                }}
              />
            ))}
          </div>
        </Section>

        {/* Tiers */}
        <Section
          title="Sponsorship tiers"
          right={
            <Button variant="outline" size="sm" className="rounded-full" onClick={addTier} data-testid="proposal-add-tier-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add tier
            </Button>
          }
        >
          {(p.tiers || []).length === 0 && (
            <div className="text-sm text-muted-foreground italic">No tiers yet — tap "Add tier".</div>
          )}
          <div className="space-y-4">
            {(p.tiers || []).map((t, i) => (
              <Card key={t.name || `tier-${i}`} className="p-4 rounded-2xl space-y-3" data-testid={`proposal-tier-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">Tier #{i + 1}</Badge>
                  <div className="flex-1" />
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => removeTier(i)} data-testid={`proposal-tier-delete-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Row label="Tier name" value={t.name} onChange={(v) => setTier(i, "name", v)} placeholder="Gold" />
                  <Row label="Price" value={t.price} onChange={(v) => setTier(i, "price", v)} placeholder="$5000p.a" />
                  <Row label="Term (optional)" value={t.term} onChange={(v) => setTier(i, "term", v)} placeholder="3 YEAR TERM" />
                </div>
                <BulletsField
                  label="Key benefits"
                  values={t.bullets || []}
                  onChange={(vals) => setTier(i, "bullets", vals)}
                />
                <PhotoField label="Tier photo" value={t.image_url} onChange={(v) => setTier(i, "image_url", v)} />
              </Card>
            ))}
          </div>
        </Section>

        {/* Meet the team */}
        <Section
          title="Meet the team"
          right={
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setP((prev) => ({
                ...prev,
                team_members: [...(prev.team_members || []), { name: "", role: "", image_url: "" }],
              }))}
              data-testid="proposal-add-team-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add member
            </Button>
          }
        >
          <RowMultiline label="Team intro" value={p.team_intro} onChange={(v) => set("team_intro", v)} rows={5} />
          <Row label="Contact email" value={p.team_email} onChange={(v) => set("team_email", v)} placeholder="derwentcc@gmail.com" />
          {(p.team_members || []).length === 0 && (
            <div className="text-sm text-muted-foreground italic">No members yet — tap "Add member".</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(p.team_members || []).map((m, i) => (
              <Card key={m.name || `member-${i}`} className="p-3 rounded-2xl space-y-2" data-testid={`proposal-team-${i}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Member #{i + 1}</Badge>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full text-red-600 hover:text-red-700"
                    onClick={() => setP((prev) => ({
                      ...prev,
                      team_members: (prev.team_members || []).filter((_, idx) => idx !== i),
                    }))}
                    data-testid={`proposal-team-delete-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Row label="Name" value={m.name} onChange={(v) => setP((prev) => {
                  const arr = [...(prev.team_members || [])];
                  arr[i] = { ...arr[i], name: v };
                  return { ...prev, team_members: arr };
                })} placeholder="Dale Clifford" />
                <Row label="Role" value={m.role} onChange={(v) => setP((prev) => {
                  const arr = [...(prev.team_members || [])];
                  arr[i] = { ...arr[i], role: v };
                  return { ...prev, team_members: arr };
                })} placeholder="President" />
                <PhotoField label="Photo" value={m.image_url} onChange={(v) => setP((prev) => {
                  const arr = [...(prev.team_members || [])];
                  arr[i] = { ...arr[i], image_url: v };
                  return { ...prev, team_members: arr };
                })} />
              </Card>
            ))}
          </div>
        </Section>

        {/* Signage reference */}
        <Section title="Signage reference">
          <RowMultiline label="Signage intro" value={p.signage_intro} onChange={(v) => set("signage_intro", v)} rows={4} />
          <p className="text-[11px] text-muted-foreground">
            Leave both photos blank to display an auto-generated diagram based on your tier list.
            Upload a photo to override either diagram.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PhotoField label="Internal signage override" value={p.signage_internal_image_url} onChange={(v) => set("signage_internal_image_url", v)} />
            <PhotoField label="Outdoor signage override" value={p.signage_outdoor_image_url} onChange={(v) => set("signage_outdoor_image_url", v)} />
          </div>
        </Section>

        {/* Sponsorship form */}
        <Section title="Sponsorship form">
          <RowMultiline label="Form intro" value={p.form_intro} onChange={(v) => set("form_intro", v)} rows={4} />
          <Row label="Return-to email (where submissions are sent)" value={p.form_return_email} onChange={(v) => set("form_return_email", v)} placeholder="derwentcc@gmail.com" />
          <div>
            <Label className="text-xs font-medium">Form header photos (up to 3)</Label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              {[0, 1, 2].map((i) => (
                <PhotoTile
                  key={`hero-photo-${i}`}
                  value={(p.form_hero_image_urls || [])[i] || ""}
                  onChange={(v) => {
                    const arr = [...(p.form_hero_image_urls || ["", "", ""])];
                    while (arr.length < 3) arr.push("");
                    arr[i] = v;
                    set("form_hero_image_urls", arr);
                  }}
                />
              ))}
            </div>
          </div>
          <BulletsField
            label="Extra sponsorship options (beyond tiers)"
            values={p.form_extra_options || []}
            onChange={(vals) => set("form_extra_options", vals)}
            hint="Examples: 'Donation of goods for raffles & prizes', 'Tailored package (negotiation)'."
          />
          {p.form_return_email && (
            <p className="text-[11px] text-muted-foreground">
              <span className="font-semibold">Note:</span> submissions are stored in the database and pressing "Send enquiry" opens the visitor's own email app pre-filled to <code>{p.form_return_email}</code>. No SMTP setup required.
            </p>
          )}
        </Section>

        <div className="pt-4 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save proposal"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Section({ title, right, children }) {
  return (
    <Card className="p-5 rounded-2xl space-y-3" data-testid={`proposal-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </Card>
  );
}

function Row({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function RowMultiline({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function BulletsField({ label, values, onChange, hint }) {
  const text = (values || []).join("\n");
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <Textarea
        rows={Math.max(3, (values || []).length + 1)}
        value={text}
        onChange={(e) => onChange(e.target.value.split("\n"))}
        placeholder="One bullet per line"
      />
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function PhotoField({ label, value, onChange }) {
  const ref = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > MAX_BYTES) { toast.error("Image must be 4MB or smaller"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex items-center gap-3 mt-1">
        <div className="relative h-20 w-24 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <>
              <img src={value} alt={label} className="max-h-full max-w-full object-cover" />
              <button
                type="button"
                onClick={() => { onChange(""); if (ref.current) ref.current.value = ""; }}
                className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                aria-label="Remove image"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          ) : (
            <ImageOff className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <input ref={ref} type="file" accept="image/png,.png" className="hidden" onChange={onFile} />
        <Button type="button" variant="outline" className="rounded-full h-8" onClick={() => ref.current?.click()}>
          <Upload className="h-3.5 w-3.5 mr-1" /> Upload
        </Button>
      </div>
    </div>
  );
}

function PhotoTile({ value, onChange }) {
  const ref = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return;
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > MAX_BYTES) { toast.error("Image must be 4MB or smaller"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="relative aspect-square rounded-2xl bg-[hsl(var(--secondary))] overflow-hidden">
      {value ? (
        <>
          <img src={value} alt="Team" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => { onChange(""); if (ref.current) ref.current.value = ""; }}
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
          >
            <X className="h-3 w-3" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="w-full h-full flex flex-col items-center justify-center text-muted-foreground hover:text-foreground gap-1"
        >
          <Upload className="h-4 w-4" />
          <span className="text-xs">Add photo</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/png,.png" className="hidden" onChange={onFile} />
    </div>
  );
}
